import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Add,
  Close,
  Delete,
  Error as ErrorIcon
} from "@dreamhub/designsystem/icons";
import DateTimeField from "@/components/controls/DateTimeField";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import { Divider } from "@/components/ui/divider";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  AddBundleRequest,
  BundleDiscountRequest,
  UpdateBundleRequest
} from "@/pages/contracts/hooks";
import { ContractRevenueItem } from "@/pages/contracts/types";
import {
  BundleDiscountError,
  bundleLineTotal,
  parseBundleDiscount,
  parseBundleQuantity
} from "@/pages/settings/pages/products-v2/bundleUtils";
import { BundleDiscountField } from "@/pages/settings/pages/products-v2/components/BundleDiscountField";
import {
  BundleServiceRow,
  BundleServiceSelection
} from "@/pages/settings/pages/products-v2/components/BundleServiceRow";
import {
  ServiceItem,
  ServiceItemType
} from "@/pages/settings/pages/products-v2/types";
import {
  BundleItemDefaults,
  bundleLineRequest,
  bundleServiceOptions,
  bundleUpdateLines,
  existingLineTotal,
  lineTotalBeforeBundleDiscount,
  splitBundleLines
} from "../bundleLines";
import {
  bundlePricingDiscountedTotal,
  bundlePricingTotal
} from "../bundlePricing";
import { formatCurrency, getCurrencySymbol } from "../consts";
import ContractField from "./ContractField";

const TRANSLATION_KEY = "contractTerms.bundle";

// Called when the server refuses the discount as larger than the bundle total.
type OnDiscountRefused = () => void;

interface DealBundleDrawerProps {
  // A Settings bundle being added, or the bundle on the deal being edited.
  bundle: {
    id: string;
    name: string;
    description: string | null;
    items: BundleItemDefaults[];
    // Adding, the Settings default; editing, the deal's own.
    isDiscountPercentage?: boolean | null;
    discountAmount?: number | null;
  };
  // The product's catalog, for each bundled service's terms and pricing.
  services: ServiceItem[];
  serviceItemTypes: ServiceItemType[];
  currency?: string | null;
  isSaving: boolean;
  onClose: () => void;
  // Adding the bundle to the deal.
  onSave?: (
    request: AddBundleRequest,
    onDiscountRefused: OnDiscountRefused
  ) => void;
  // Editing a bundle already on the deal: its lines and start date.
  edit?: {
    lines: ContractRevenueItem[];
    servicesStartDate: string;
    onSave: (
      request: UpdateBundleRequest,
      onDiscountRefused: OnDiscountRefused
    ) => void;
    onDelete: () => void;
  };
}

export const DealBundleDrawer: React.FC<DealBundleDrawerProps> = ({
  bundle,
  services,
  serviceItemTypes,
  currency,
  isSaving,
  onClose,
  onSave,
  edit
}) => {
  const { t } = useTranslation();
  // The services and terms the drawer offers, taken when it opens: a catalog
  // that changes underneath can't drop a line the AE never unchecked.
  const [listed] = useState(() => bundleServiceOptions(bundle, services));
  // Only a line of a service the drawer lists is editable; every other line is
  // shown and kept as it is.
  const [lines] = useState(() =>
    splitBundleLines(
      edit?.lines ?? [],
      listed.map(({ service }) => service)
    )
  );
  // A service whose only line the drawer can't edit shows as that line.
  const options = useMemo(
    () =>
      listed.filter(
        ({ service }) =>
          lines.editable.has(service.id) ||
          !lines.fixed.some((line) => line.serviceItemId === service.id)
      ),
    [listed, lines]
  );

  const previousStartDate = edit?.servicesStartDate.slice(0, 10) ?? "";
  const lineOf = (serviceId: string) => lines.editable.get(serviceId);
  // A service's line on the deal as the drawer shows it, or its defaults.
  const selectionOf = ({
    service,
    item
  }: (typeof options)[number]): BundleServiceSelection => {
    const line = lineOf(service.id);
    return {
      termId: line?.serviceItemPricingId ?? item.serviceItemPricingId,
      quantity: String(line?.quantity ?? item.quantity)
    };
  };

  // Adding, no date is suggested: the AE picks the one the customer starts on.
  const [startDate, setStartDate] = useState(previousStartDate);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  // Adding, every service starts checked with its Settings quantity and term;
  // editing, the services with a line on the deal start checked as they are.
  const [selections, setSelections] = useState<
    Record<string, BundleServiceSelection>
  >(() =>
    Object.fromEntries(
      options
        .filter(({ service }) => !edit || lineOf(service.id))
        .map((option) => [option.service.id, selectionOf(option)])
    )
  );
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

  // The bundle's discount starts as the Settings default when adding, and as
  // the deal's own when editing. A Settings amount is used as it is, in the
  // contract's currency, as catalog prices are.
  const [isDiscountShown, setIsDiscountShown] = useState(
    Boolean(bundle.discountAmount)
  );
  const [isDiscountPercentage, setIsDiscountPercentage] = useState(
    bundle.isDiscountPercentage ?? true
  );
  const [discountValue, setDiscountValue] = useState(
    bundle.discountAmount ? String(bundle.discountAmount) : ""
  );
  // A number field reports unparseable text ("-", "1e") as empty, which would
  // otherwise read as no discount and clear the bundle's on save.
  const [isDiscountUnparseable, setIsDiscountUnparseable] = useState(false);
  // The server's refusal, kept while the total and discount it refused stay.
  const [refusedDiscount, setRefusedDiscount] = useState<{
    total: number;
    value: string;
  } | null>(null);

  const typeName = (typeId: number) =>
    serviceItemTypes.find(({ id }) => id === typeId)?.name ?? "";

  const toggleService = (option: (typeof options)[number], checked: boolean) =>
    setSelections((current) => {
      const next = { ...current };
      if (checked) {
        // Checked again, a service gets its line's values back, or its defaults.
        next[option.service.id] = selectionOf(option);
      } else {
        delete next[option.service.id];
      }
      return next;
    });

  const updateSelection = (
    serviceId: string,
    change: Partial<BundleServiceSelection>
  ) =>
    setSelections((current) => ({
      ...current,
      [serviceId]: { ...current[serviceId], ...change }
    }));

  const checked = options.flatMap(({ service }) => {
    const selection = selections[service.id];
    const term = service.pricing.find(({ id }) => id === selection?.termId);
    return selection && term ? [{ service, term, selection }] : [];
  });

  const total = bundlePricingTotal([
    ...lines.fixed.map(lineTotalBeforeBundleDiscount),
    ...checked.map(({ service, term, selection }) => {
      const quantity = parseBundleQuantity(selection.quantity) ?? 0;
      const line = lineOf(service.id);
      return line
        ? existingLineTotal(service, term, quantity, line)
        : bundleLineTotal(
            service.pricingModel,
            service.chargeType,
            term,
            quantity
          );
    })
  ]);
  const serviceCount = checked.length + lines.fixed.length;

  // An amount fits up to the total's size, a downsell's credit included, as the
  // server checks it.
  const discount: { amount: number | null; error: BundleDiscountError | null } =
    !isDiscountShown
      ? { amount: null, error: null }
      : isDiscountUnparseable
        ? {
            amount: null,
            error: isDiscountPercentage ? "percentage" : "negative"
          }
        : parseBundleDiscount(
            discountValue,
            isDiscountPercentage,
            Math.abs(total)
          );
  const isDiscountRefused =
    refusedDiscount?.total === total && refusedDiscount.value === discountValue;
  const discountError: BundleDiscountError | null =
    discount.error ?? (isDiscountRefused ? "aboveTotal" : null);
  const hasDiscount = Boolean(discount.amount);
  const discountedTotal = bundlePricingDiscountedTotal(total, {
    isDiscountPercentage,
    discountAmount: discount.amount
  });

  const hasNoServices = serviceCount === 0;
  const hasInvalidQuantity = checked.some(
    ({ selection }) => parseBundleQuantity(selection.quantity) === null
  );

  const handleSave = () => {
    setIsSubmitAttempted(true);
    if (
      isSaving ||
      !startDate ||
      hasNoServices ||
      hasInvalidQuantity ||
      discountError
    ) {
      return;
    }
    // Always sent: a write that leaves both fields out keeps the stored
    // discount, or, when adding, takes the Settings default and converts an
    // amount into the contract's currency, which a deal doesn't do.
    const discountRequest: BundleDiscountRequest = {
      isDiscountPercentage: hasDiscount ? isDiscountPercentage : null,
      discountAmount: hasDiscount ? discount.amount : null
    };
    const onDiscountRefused = () =>
      setRefusedDiscount({ total, value: discountValue });
    if (edit) {
      edit.onSave(
        {
          servicesStartDate: startDate,
          lines: bundleUpdateLines(
            checked.map(({ service, term, selection }) => ({
              service,
              term,
              quantity: parseBundleQuantity(selection.quantity) ?? 0
            })),
            lines,
            { startDate, previousStartDate }
          ),
          ...discountRequest
        },
        onDiscountRefused
      );
      return;
    }
    onSave?.(
      {
        sourceBundleId: bundle.id,
        servicesStartDate: startDate,
        lines: checked.map(({ service, term, selection }) =>
          bundleLineRequest(
            service,
            term,
            parseBundleQuantity(selection.quantity) ?? 0,
            startDate
          )
        ),
        ...discountRequest
      },
      onDiscountRefused
    );
  };

  return (
    <Drawer
      open
      direction="right"
      fixed
      modal={false}
      disablePreventScroll={false}
      noBodyStyles
      dismissible={false}
    >
      <AppDrawerContent data-test="deal-bundle-drawer">
        <DrawerHeader>
          <DrawerTitle>
            <Text variant="lgSubtitle">
              {t(`${TRANSLATION_KEY}.${edit ? "editTitle" : "addTitle"}`)}
            </Text>
          </DrawerTitle>
          <IconButton
            onClick={onClose}
            // A save still in flight closes the drawer when it lands.
            disabled={isSaving}
            variant="iconNoBg"
            size="sm"
            aria-label={t("close")}
            data-test="deal-bundle-drawer-close-button"
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <div className="flex w-full flex-1 flex-col overflow-y-scroll px-5 py-5">
          <div className="flex flex-col gap-4">
            <div className="flex w-full items-start justify-between gap-4">
              <Text variant="body" data-test="deal-bundle-drawer-name">
                {bundle.name}
              </Text>
              <Text variant="bodySecondary" className="shrink-0">
                {t(`${TRANSLATION_KEY}.label`)}
              </Text>
            </div>
            {bundle.description && (
              <Text variant="bodySecondary">{bundle.description}</Text>
            )}
          </div>

          <Divider dividerYPadding="16" />

          <div className="flex flex-col gap-1">
            <ContractField
              label={t(`${TRANSLATION_KEY}.servicesStartDate`)}
              required
            >
              <DateTimeField
                type="date"
                value={startDate}
                onChange={setStartDate}
                isOpen={isDatePickerOpen}
                onOpenChange={setIsDatePickerOpen}
                name="bundle-services-start-date"
                placeholder={t("contractTerms.startDate")}
                required
              />
            </ContractField>
            {isSubmitAttempted && !startDate && (
              <Text
                variant="smBodyError"
                className="pl-1"
                data-test="deal-bundle-drawer-start-date-error"
              >
                {t("requiredField")}
              </Text>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <Text variant="bodySecondary" className="pl-1">
              {t(`${TRANSLATION_KEY}.services`)}
            </Text>
            <div
              className="overflow-hidden rounded-xl border-[0.5px] border-border-border1 bg-surface-field-default"
              data-test="deal-bundle-drawer-services"
            >
              <div className="flex h-9 items-end border-b-[0.5px] border-border-border1 pb-1">
                <div className="w-10 shrink-0" />
                <Text variant="smBodySecondary" className="flex-1 px-2.5">
                  {t(`${TRANSLATION_KEY}.service`)}
                </Text>
                <Text
                  variant="smBodySecondary"
                  className="w-[70px] px-2.5 text-right"
                >
                  {t("contractTerms.quantity")}
                </Text>
              </div>
              {options.map((option) => (
                <BundleServiceRow
                  key={option.service.id}
                  service={option.service}
                  typeName={typeName(option.service.typeId)}
                  selection={selections[option.service.id]}
                  onToggle={(isChecked) => toggleService(option, isChecked)}
                  onTermChange={(termId) =>
                    updateSelection(option.service.id, { termId })
                  }
                  onQuantityChange={(quantity) =>
                    updateSelection(option.service.id, { quantity })
                  }
                />
              ))}
              {lines.fixed.map((line) => (
                <FixedLineRow key={line.id} line={line} />
              ))}
            </div>
            {isSubmitAttempted && hasNoServices && (
              <div
                className="flex items-center gap-1 pl-1"
                data-test="deal-bundle-drawer-no-services-error"
              >
                <IconContainer as="span" color="error">
                  <ErrorIcon />
                </IconContainer>
                <Text variant="smBodyError">
                  {t(`${TRANSLATION_KEY}.noServicesError`)}
                </Text>
              </div>
            )}
          </div>

          <div className="pt-4">
            {isDiscountShown ? (
              <BundleDiscountField
                value={discountValue}
                isPercentage={isDiscountPercentage}
                error={discountError}
                currencySymbol={getCurrencySymbol(currency)}
                onValueChange={setDiscountValue}
                onUnparseableChange={setIsDiscountUnparseable}
                onTypeChange={(isPercentage) => {
                  // The tabs re-report the active type on a re-click.
                  if (isPercentage === isDiscountPercentage) return;
                  setIsDiscountPercentage(isPercentage);
                  setDiscountValue("");
                  setIsDiscountUnparseable(false);
                }}
                onRemove={() => {
                  setIsDiscountShown(false);
                  setDiscountValue("");
                  setIsDiscountUnparseable(false);
                }}
              />
            ) : (
              <Button
                variant="tertiary"
                onClick={() => setIsDiscountShown(true)}
                data-test="deal-bundle-drawer-add-discount"
              >
                <IconContainer as="span" size="sm" color="current">
                  <Add />
                </IconContainer>
                {t("settings.product.bundleDrawer.discount.add")}
              </Button>
            )}
          </div>

          <Divider dividerYPadding="16" />

          <div
            className="flex flex-col pl-1"
            data-test="deal-bundle-drawer-total"
          >
            <Text variant="bodySecondary">{t("contractTerms.total")}</Text>
            <div className="flex h-8 items-center justify-between">
              <Text variant="body">
                {t(`${TRANSLATION_KEY}.serviceCount`, {
                  count: serviceCount
                })}
              </Text>
              <div className="flex items-center gap-1">
                {hasDiscount && (
                  <Text
                    variant="bodyTertiary"
                    className="line-through"
                    data-test="deal-bundle-drawer-original-total"
                  >
                    {formatCurrency(total, currency)}
                  </Text>
                )}
                <Text
                  variant="body"
                  data-test="deal-bundle-drawer-total-amount"
                >
                  {formatCurrency(discountedTotal, currency)}
                </Text>
              </div>
            </div>
          </div>
        </div>

        <DrawerFooter>
          <div className="flex w-full justify-between gap-2 border-t-[0.5px] border-border-border1 p-4">
            {edit && (
              <Button
                variant="tertiary"
                destructive
                onClick={edit.onDelete}
                disabled={isSaving}
                data-test="deal-bundle-drawer-delete-button"
              >
                <IconContainer as="span" size="sm" color="current">
                  <Delete />
                </IconContainer>
                {t(`${TRANSLATION_KEY}.deleteBundle`)}
              </Button>
            )}
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondaryNoBg"
                onClick={onClose}
                disabled={isSaving}
                data-test="deal-bundle-drawer-cancel-button"
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                data-test="deal-bundle-drawer-submit-button"
              >
                {edit ? t("save") : t(`${TRANSLATION_KEY}.add`)}
              </Button>
            </div>
          </div>
        </DrawerFooter>
      </AppDrawerContent>
    </Drawer>
  );
};

// A bundle line the drawer can't edit: shown checked, and edited from the
// table instead.
const FixedLineRow: React.FC<{ line: ContractRevenueItem }> = ({ line }) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center border-b-[0.5px] border-border-border1 last:border-b-0"
      data-test={`deal-bundle-drawer-fixed-line-${line.id}`}
    >
      <div className="flex w-10 shrink-0 justify-center px-1.5">
        <Checkbox
          checked
          disabled
          aria-label={line.serviceItemName ?? undefined}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-2.5 py-3">
        <Text as="span" variant="body" className="block truncate">
          {line.serviceItemName}
        </Text>
        <Text as="span" variant="smBodySecondary" className="block truncate">
          {t(`${TRANSLATION_KEY}.fixedService`)}
        </Text>
      </div>
      {line.quantity != null && (
        <Text variant="body" className="w-[70px] shrink-0 px-2.5 text-right">
          {line.quantity}
        </Text>
      )}
    </div>
  );
};
